import React, { useEffect, useState } from 'react';
import {
    Anchor,
    Box,
    DataTable,
    Heading,
    Page,
    PageContent,
    Pagination,
} from 'grommet';
import { useLoaderData } from 'react-router-dom';
import { API } from '@enrich/shared-lib';
import QueryString from 'qs';

export const SchoolActiveStudents = () => {
    const school = useLoaderData();
    const stepOptions = [25, 50, 100];
    const [students, setStudents] = useState([]);
    const [page, setPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(stepOptions[0]);
    const [sort, setSort] = useState();

    useEffect(() => {
        if (!page) return;
        let isActive = true;

        (async () => {
            const response = await loadStudents({
                schoolId: school.id,
                page,
                pageSize,
                sort,
            });

            if (isActive) {
                setPage(response.meta.pagination.page);
                setStudents(response.data);
                setTotalItems(response.meta.pagination.total);
            }
        })();

        return () => {
            isActive = false;
        };
    }, [page, pageSize, sort]);

    const dataColumns = [
        {
            header: 'Name',
            property: 'attributes.lastName',
            render: (contact) => (
                <Anchor
                    key={contact.id}
                    href={`/contacts/${contact.id}`}
                    label={`${contact.attributes.firstName} ${contact.attributes.lastName}`}
                />
            ),
            sortable: true,
        },
        {
            header: 'Graduation',
            property: 'attributes.graduationYear',
            render: (contact) => {
                const semester = contact.attributes.graduationSemester;
                const formattedSemester = semester
                    ? semester[0].toUpperCase() + semester.slice(1)
                    : '';
                return `${formattedSemester} ${contact.attributes.graduationYear}`;
            },
            sortable: true,
        },
        {
            header: 'Hometown',
            property: 'attributes.hometown',
        },
        {
            header: 'Majors',
            property: 'attributes.majors',
            render: (contact) => {
                const majors = contact?.attributes.majors?.data?.map(
                    (major) => major.attributes.name,
                );
                return majors?.length > 0 ? majors.join(', ') : 'N/A';
            },
        },
    ];

    const handlePageChange = ({ page, startIndex, endIndex }) => {
        const newPageSize = endIndex - startIndex;
        if (pageSize !== newPageSize) {
            setPageSize(newPageSize);
            setPage(1);
            return;
        }
        setPage(page);
    };

    const handleSort = ({ property, direction }) => {
        setSort({ property, direction });
        setPage(1);
    };

    return (
        <Page
            kind="full"
            pad={{ top: '30px', horizontal: 'medium', bottom: '30px' }}>
            <PageContent gap="medium">
                <Box
                    direction="row-responsive"
                    justify="between"
                    align="center">
                    <Box>
                        <Heading level="2">{school.attributes.name}</Heading>
                        <Heading level="3" margin={{ top: 'none' }}>
                            Active Students
                        </Heading>
                    </Box>
                    <Box>
                        <Anchor
                            href={`/schools/${school.id}`}
                            label="← Back to School"
                        />
                    </Box>
                </Box>
                <Box>
                    <DataTable
                        primaryKey="id"
                        sort={sort}
                        onSort={handleSort}
                        border="bottom"
                        data={students}
                        columns={dataColumns}
                    />
                </Box>
                <Box align="center" justify="center" pad="small">
                    <Pagination
                        numberItems={totalItems}
                        onChange={({ page, startIndex, endIndex }) => {
                            handlePageChange({
                                page,
                                startIndex,
                                endIndex,
                            });
                        }}
                        step={pageSize}
                        stepOptions={stepOptions}
                        summary={true}
                    />
                </Box>
            </PageContent>
        </Page>
    );
};

export async function loader({ params }) {
    const query = QueryString.stringify({
        fields: ['id', 'name'],
    });

    try {
        const response = await API.get(
            `/api/schools/${params.schoolId}?${query}`,
        );
        return response.data.data;
    } catch (error) {
        console.log(error);
        return {};
    }
}

const SORT_FIELD_MAP = {
    'attributes.lastName': 'lastName',
    'attributes.graduationYear': 'graduationYear',
    'attributes.hometown': 'hometown',
};

const loadStudents = async ({ schoolId, page, pageSize, sort } = {}) => {
    const sortField = sort && SORT_FIELD_MAP[sort.property];
    const query = QueryString.stringify({
        filters: {
            enrichProgramStatus: 'active',
            school: schoolId,
        },
        populate: ['majors'],
        pagination: page && pageSize ? { page, pageSize } : undefined,
        sort: sortField
            ? [`${sortField}:${sort.direction}`]
            : ['updatedAt:desc'],
    });

    try {
        const response = await API.get(`/api/contacts?${query}`);
        return response.data;
    } catch (error) {
        console.log(error);
    }

    return { data: [], meta: { pagination: { total: 0, page: 1 } } };
};
